import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import * as yup from "yup";

import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Label } from "@/components/Form/Base/label.component";
import {
    onNumericKeyDownHandler,
    toNumeric
} from "@/components/Form/RHF/FormFields/NumericField/NumericField.hooks";
import { Container } from "@/components/General/Grid/container.component";
import { Messages } from "@/components/General/Messages/messages.container";
import { CartItemModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { clearMessageById } from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { GiftCardType } from "@/utils/enums";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import * as RHFGeneralValidationRules from "@/utils/validation/schemas/general.RHF";
import * as RHFGiftCardValidationRules from "@/utils/validation/schemas/giftCard.RHF";

import { AmountField } from "../Form/RHF/FormFields/AmountField/amountField.container";
import { CreatableSelectField } from "../Form/RHF/FormFields/CreatableSelectField/creatableSelectField.component";
import { BaseOption } from "../Form/RHF/FormFields/SelectField/selectField.component";
import { TextAreaField } from "../Form/RHF/FormFields/TextAreaField/textAreaField.component";
import { useOmniForm } from "../Form/RHF/Utils/useOmniForm";
import GiftCardTypesPartial from "./partials/giftCardType.component";
import {
    GiftCard,
    RequiredGiftCardSchema
} from "./partials/giftCardType.schema";

type CreateGiftCardProps = {
    isLoading?: boolean;
    cartProduct?: CartItemModel;
    onSubmit: (request: CartModule.GiftCardRequest) => Promise<any>;
    cards: Array<GiftCard>;
};

const FORM_ID = "createGiftCard";

const CreateGiftCardFormValidationSchema = yup
    .object({
        amountInEuros: RHFGiftCardValidationRules.giftCardAmountInEuros,
        quantity: RHFGiftCardValidationRules.giftCardQuantity,
        personalMessage: RHFGeneralValidationRules.optionalMessage(115)
    })
    .concat(RequiredGiftCardSchema);

type FormValues = yup.InferType<typeof CreateGiftCardFormValidationSchema>;

type AmountOptions = BaseOption<number>;

const predefinedOptions: Array<AmountOptions> = [
    10, 15, 25, 50, 75, 100, 150, 200, 250
].map((v) => ({
    value: v,
    label: v.toString()
}));

const CreateGiftCard = ({
    isLoading = false,
    onSubmit,
    cards,
    cartProduct
}: CreateGiftCardProps) => {
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            sku:
                cartProduct?.sku ||
                cards.find((c) => c.type === GiftCardType.Digital)?.sku,
            quantity: cartProduct?.quantity || 1,
            amountInEuros: cartProduct?.salesPrice,
            personalMessage: cartProduct?.giftCardPersonalMessage || ""
        },
        resolver: yupResolver(CreateGiftCardFormValidationSchema)
    });

    const onFormSubmit = async (formValues: FormValues) => {
        clearMessageById("create-giftcard-failure");

        const values: CartModule.GiftCardRequest = {
            personalMessage: formValues.personalMessage,
            quantity: formValues.quantity,
            amountInEuros: formValues.amountInEuros,
            sku: formValues.sku
        };

        if (onSubmit) {
            onSubmit(values).then(() => {
                methods.reset({
                    sku: cards.find((c) => c.type === GiftCardType.Digital)
                        ?.sku,
                    quantity: 1,
                    amountInEuros: undefined,
                    personalMessage: ""
                });
            });
        }
    };

    const fysicalCards = useMemo(
        () => cards.filter((c) => c.type !== GiftCardType.Digital),
        [cards]
    );

    const digitalCards = useMemo(
        () => cards.filter((c) => c.type === GiftCardType.Digital),
        [cards]
    );

    return (
        <>
            <Messages messageid={"create-giftcard-failure"} />
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <FormProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onFormSubmit)}
                        >
                            <Container>
                                <FormGroup>
                                    <Label className={cn("tw:block tw:mb-0")}>
                                        {t("general.form.giftcard.type.label")}
                                        <small className={cn("tw:italic")}>
                                            *
                                        </small>
                                    </Label>
                                    <GiftCardTypesPartial<FormValues>
                                        required
                                        cards={digitalCards}
                                        microCopy={t(
                                            "general.form.giftcard.type.digital"
                                        )}
                                    />
                                    <GiftCardTypesPartial<FormValues>
                                        required
                                        cards={fysicalCards}
                                        microCopy={t(
                                            "general.form.giftcard.type.fysical"
                                        )}
                                    />
                                </FormGroup>
                                <CreatableSelectField<FormValues, AmountOptions>
                                    name={"amountInEuros"}
                                    label={t(
                                        "general.form.giftcard.amountInEuros.label"
                                    )}
                                    placeholder={t(
                                        "general.form.giftcard.amountInEuros.placeholder"
                                    )}
                                    optionsList={predefinedOptions}
                                    required={true}
                                    inputProps={{
                                        autoComplete: "off",
                                        pattern: "[0-9]*",
                                        type: "number",
                                        inputMode: "numeric",
                                        onKeyDown: onNumericKeyDownHandler,
                                        mutateOnBlur: (inputValue: string) =>
                                            toNumeric(
                                                inputValue,
                                                true,
                                                predefinedOptions.at(0)!.value
                                            )
                                    }}
                                    disabled={isLoading}
                                />
                                <AmountField<FormValues>
                                    name={"quantity"}
                                    label={t(
                                        "general.form.giftcard.quantity.label"
                                    )}
                                    max={999}
                                    min={1}
                                    required
                                    disabled={isLoading}
                                />
                                <TextAreaField<FormValues>
                                    name="personalMessage"
                                    label={t(
                                        "general.form.giftcard.message.label"
                                    )}
                                    required={false}
                                    autoComplete={"off"}
                                    placeholder={t(
                                        "general.form.giftcard.message.placeholder"
                                    )}
                                    maxLength={115}
                                    disabled={isLoading}
                                />
                                <Button
                                    test="createGiftCard-button-submit"
                                    type={"submit"}
                                    loading={isLoading}
                                >
                                    {t("general.form.giftcard.order")}
                                </Button>
                            </Container>
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
        </>
    );
};

export default CreateGiftCard;
